import { JSX, useMemo } from 'react';
import { ComponentRendering, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { RenderingProps } from './shared.types';
import { Alert } from '@ahc-digital/component-library';

type MissingDsReason =
  | 'no-datasource-assigned'
  | 'datasource-unresolved'
  | 'empty-content'
  | undefined;

type Props = {
  alertType?: 'info' | 'warning' | 'error' | 'success';
  rendering?: string | ComponentRendering | RenderingProps['rendering'];
  datasource?: string;
  fieldName?: string;
  reason?: MissingDsReason;
  className?: string;
};

function getRenderingName(rendering: Props['rendering']): string | undefined {
  if (!rendering) return undefined;
  if (typeof rendering === 'string') return rendering;

  return rendering.componentName ?? undefined;
}

const getMessage = (componentName?: string, reason?: MissingDsReason, datasource?: string) => {
  const rendering = componentName || 'Component';
  switch (reason) {
    case 'no-datasource-assigned':
      return `<p>DataSource for Rendering "<b>${rendering}</b>" is not set. Please set the DataSource under "<b>More</b>" > "<b>Edit component properties</b>" > "<b>DataSource</b>"</p>`;
    case 'datasource-unresolved':
      return `<p>DataSource for Rendering "<b>${rendering}</b>" could not be resolved (missing or wrong language/version).</p><p>Please confirm path to DataSource: "<b>${datasource}</b>" exists.</p>`;
    default:
      return `<p>DataSource for Rendering "<b>${rendering}</b>" is not available.</p>`;
  }
};

export const PromptForMissingDatasource = (props: Props): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = !!sitecoreContext?.pageEditing;

  const renderingName = useMemo(() => getRenderingName(props.rendering), [props.rendering]);

  const message = useMemo(
    () => getMessage(renderingName, props.reason, props.datasource),
    [renderingName, props.reason, props.datasource]
  );

  if (!isEditing) {
    return <></>;
  }

  return (
    <Alert
      alertType={props.alertType || 'warning'}
      shouldHideIcon={false}
      isDismissible={false}
      alertFunction="dismissible"
      primaryAlertText={message}
      role="status"
      aria-live="polite"
      className={props.className}
    />
  );
};

export const PromptForMissingFieldValue = (props?: Props): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing;

  if (isEditing) {
    return (
      <Alert
        alertType="error"
        shouldHideIcon={false}
        isDismissible={false}
        alertFunction="dismissible"
        primaryAlertText={`<p>Required &quot;<b>${
          props?.fieldName ?? 'Field'
        }</b>&quot; field on rendering &quot;<b>${
          props?.rendering ?? 'Component'
        }</b>&quot; is not set</p>`}
      />
    );
  }

  return <></>;
};
